import { useEffect, useRef, useState } from 'react';
import { Group } from '../../apis/api-client';
import { groupsClient } from '../../apis/client-instances';
import { Autocomplete, Chip, CircularProgress, TextField } from '../ui';

const SEARCH_PAGE_SIZE = 25;
const DEBOUNCE_MS = 250;
const DEFAULT_MIN_WIDTH = 240;

export interface GroupAutocompleteProps {
  /** Single-select value, or array when `multiple` is true. */
  value: Group | Group[] | null;
  onChange: (value: Group | Group[] | null) => void;
  multiple?: boolean;
  /** Optional workspace filter passed to the server. */
  workspaceId?: number;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: any;
}

/**
 * Lazy-loaded group picker. Server-side search, debounced typing, optional
 * workspace scoping. Supports single- or multi-select.
 *
 * The caller is responsible for hydrating any initial selection (e.g.
 * resolving a stored ID into a Group object) — see groupsClient.searchGroups
 * with the `ids` param for bulk resolution.
 */
export default function GroupAutocomplete({
  value,
  onChange,
  multiple = false,
  workspaceId,
  label,
  placeholder,
  size = 'small',
  fullWidth = false,
  disabled = false,
  required = false,
  error = false,
  helperText,
  sx,
}: GroupAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Group[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const id = ++reqIdRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await groupsClient.searchGroups(
          workspaceId ?? undefined,
          inputValue || undefined,
          1,
          SEARCH_PAGE_SIZE,
          undefined
        );
        if (id !== reqIdRef.current) return;
        setOptions(result.items ?? []);
      } catch {
        if (id !== reqIdRef.current) return;
        setOptions([]);
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, open, workspaceId]);

  // Whether to show the clear (X) button — only meaningful when there's
  // a selection to clear. For multi-select, always show so the user can
  // empty the chips list.
  const hasSelection = multiple
    ? Array.isArray(value) && value.length > 0
    : value != null;

  return (
    <Autocomplete<Group, boolean, false, false>
      multiple={multiple}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value as any}
      onChange={(_e, newValue) => onChange(newValue as any)}
      inputValue={inputValue}
      onInputChange={(_e, val) => setInputValue(val)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(opt, val) => opt.id === (val as Group)?.id}
      noOptionsText={inputValue ? 'No groups match' : 'Type to search'}
      renderTags={(items, getTagProps) =>
        items.map((option, index) => (
          <Chip
            label={`${option.name}${option.memberCount != null ? ` (${option.memberCount})` : ''}`}
            size="small"
            color="secondary"
            variant="outlined"
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      sx={{
        minWidth: DEFAULT_MIN_WIDTH,
        '& .MuiAutocomplete-clearIndicator': {
          visibility: hasSelection ? 'visible' : 'hidden',
        },
        ...sx,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
